import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSystemData } from '../contexts/SystemContext';
import { apiClient } from '../services/api';
import { Prompt, PromptCreate, PromptUpdate } from '../types/api';
import { FileText, RefreshCw, Search, Eye, Filter, Plus, Edit, Trash2, X, Save, Zap, ChevronDown } from 'lucide-react';

// Multi-Select Dropdown Component
interface MultiSelectProps {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  label: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ options, selected, onChange, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const removeItem = (item: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(i => i !== item));
  };

  return (
    <div ref={dropdownRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div
        className="w-full min-h-[42px] px-3 py-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 cursor-pointer bg-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1 items-center">
          {selected.length > 0 ? (
            selected.map(item => (
              <span
                key={item}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
              >
                {item}
                <button
                  onClick={(e) => removeItem(item, e)}
                  className="ml-1 hover:text-blue-600"
                  type="button"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          ) : (
            <span className="text-gray-400 text-sm">{placeholder || `Select ${label.toLowerCase()}...`}</span>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {options.length > 0 ? (
            options.map(option => (
              <div
                key={option}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                onClick={() => toggleOption(option)}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => {}}
                  className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-900">{option}</span>
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

const Prompts: React.FC = () => {
  const { prompts, isLoading, forceRefreshPrompts } = useSystemData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingEmbeddings, setIsGeneratingEmbeddings] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // 12 prompts per page 

  // Form states
  const [createForm, setCreateForm] = useState<PromptCreate>({
    title: '',
    content: '',
    category: [],
    team: [],
    description: '',
    created_by: [],
    usage_guide: ''
  });

  const [editForm, setEditForm] = useState<PromptUpdate>({});



  const handleGenerateEmbeddings = async () => {
    try {
      setIsGeneratingEmbeddings(true);
      const result = await apiClient.generateNewEmbeddings();
      
      if (result.success) {
        // Show success message
        alert(`Successfully generated embeddings for ${result.new_prompts_count} new prompts!`);
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error generating embeddings:', error);
      alert('Failed to generate embeddings. Please try again.');
    } finally {
      setIsGeneratingEmbeddings(false);
    }
  };

  // Filter prompts based on search term and category
  const filteredPrompts = useMemo(() => {
    let filtered = prompts?.prompts || [];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(prompt => 
        prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prompt.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (prompt.description && prompt.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (prompt.usage_guide && prompt.usage_guide.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (prompt.category && prompt.category.some(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()))) ||
        (prompt.team && prompt.team.some(team => team.toLowerCase().includes(searchTerm.toLowerCase()))) ||
        (prompt.created_by && prompt.created_by.some(creator => creator.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }
    
    // Apply category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(prompt => 
        prompt.category && prompt.category.includes(filterCategory)
      );
    }
    
    return filtered;
  }, [prompts?.prompts, searchTerm, filterCategory]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredPrompts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPrompts = filteredPrompts.slice(startIndex, endIndex);

  // Reset to first page when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Update pagination when search or filter changes
  React.useEffect(() => {
    resetPagination();
  }, [searchTerm, filterCategory]);

  // Get unique categories for filter dropdown 
  const categories = useMemo(() => {
    if (prompts?.prompts && prompts.prompts.length > 0) {
      // Extract categories from arrays
      const extracted_categories = prompts.prompts.reduce((acc: string[], prompt) => {
        if (prompt.category && prompt.category.length > 0) {
          prompt.category.forEach(cat => {
            if (!acc.includes(cat)) {
              acc.push(cat);
            }
          });
        }
        return acc;
      }, []) || [];
      return extracted_categories.sort();
    } else {
      return [];
    }
  }, [prompts?.prompts]);

  // Get unique teams for dropdown
  const teams = useMemo(() => {
    if (prompts?.prompts && prompts.prompts.length > 0) {
      // Extract teams from arrays
      const extracted_teams = prompts.prompts.reduce((acc: string[], prompt) => {
        if (prompt.team && prompt.team.length > 0) {
          prompt.team.forEach(team => {
            if (!acc.includes(team)) {
              acc.push(team);
            }
          });
        }
        return acc;
      }, []) || [];
      return extracted_teams.sort();
    } else {
      return [];
    }
  }, [prompts?.prompts]);

  const handleCreatePrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await apiClient.createPrompt(createForm);
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        content: '',
        category: [],
        team: [],
        description: '',
        created_by: [],
        usage_guide: ''
      });
      await forceRefreshPrompts();
    } catch (error) {
      console.error('Error creating prompt:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPrompt) return;
    
    try {
      setIsSubmitting(true);
      await apiClient.updatePrompt(editingPrompt.id, editForm);
      setShowEditModal(false);
      setEditingPrompt(null);
      setEditForm({});
      await forceRefreshPrompts();
    } catch (error) {
      console.error('Error updating prompt:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePrompt = async (promptId: string) => {
    if (!window.confirm('Are you sure you want to delete this prompt? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.deletePrompt(promptId);
      await forceRefreshPrompts();
    } catch (error) {
      console.error('Error deleting prompt:', error);
    }
  };

  const openEditModal = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setEditForm({
      title: prompt.title,
      content: prompt.content,
      category: prompt.category,
      team: prompt.team,
      description: prompt.description,
      created_by: prompt.created_by,
      usage_guide: prompt.usage_guide
    });
    setShowEditModal(true);
  };

  if (isLoading && !prompts) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notion Prompt Library</h1>
          <p className="text-gray-600">
            {prompts?.total_count || 0} prompts in Notion database
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={forceRefreshPrompts}
            disabled={isLoading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh from Notion</span>
          </button>
          <button
            onClick={handleGenerateEmbeddings}
            disabled={isGeneratingEmbeddings || isLoading}
            className="btn-secondary flex items-center space-x-2"
            title="Generate embeddings for new prompts"
          >
            <Zap className={`h-4 w-4 ${isGeneratingEmbeddings ? 'animate-pulse' : ''}`} />
            <span>
              {isGeneratingEmbeddings ? 'Generating...' : 'Generate Embeddings'}
            </span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Create Prompt</span>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search prompt."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="sm:w-48">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="stat-card">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Prompts</p>
              <p className="text-2xl font-semibold text-gray-900">{prompts?.total_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <Filter className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Categories</p>
              <p className="text-2xl font-semibold text-gray-900">{categories.length}</p>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <Search className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Showing Results</p>
              <p className="text-2xl font-semibold text-gray-900">
                {paginatedPrompts.length} of {prompts?.total_count || filteredPrompts.length}
              </p>
            </div>
          </div>
        </div>


      </div>

      {/* Prompts Grid */}
      {paginatedPrompts.length > 0 ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {paginatedPrompts.map((prompt) => (
            <div key={prompt.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-medium text-gray-900 line-clamp-2">
                  {prompt.title}
                </h3>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setSelectedPrompt(prompt)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openEditModal(prompt)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                    title="Edit Prompt"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeletePrompt(prompt.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Delete Prompt"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                {prompt.category && prompt.category.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {prompt.category.map((cat, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {cat}
                      </span>
                    ))}
                  </div>
                )}
                {prompt.team && prompt.team.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {prompt.team.map((team, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {team}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {prompt.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {prompt.description}
                </p>
              )}
              
              <p className="text-sm text-gray-600 line-clamp-3">
                {prompt.content?.substring(0, 200)}...
              </p>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredPrompts.length)}</span> of{' '}
                  <span className="font-medium">{filteredPrompts.length}</span> results
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            page === currentPage
                              ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span
                          key={page}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </>
      ) : (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Prompts Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterCategory !== 'all' 
              ? 'No prompts match your search criteria.'
              : 'No prompts available in the Notion database.'}
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center space-x-2 mx-auto"
            >
              <Plus className="h-4 w-4" />
              <span>Create First Prompt</span>
            </button>
          </div>
        </div>
      )}

      {/* Create Prompt Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Prompt</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreatePrompt} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                />
              </div>
              
              <MultiSelect
                label="Categories"
                options={categories}
                selected={createForm.category || []}
                onChange={(selected) => setCreateForm({ ...createForm, category: selected })}
                placeholder="Select categories..."
              />

              <MultiSelect
                label="Teams"
                options={teams}
                selected={createForm.team || []}
                onChange={(selected) => setCreateForm({ ...createForm, team: selected })}
                placeholder="Select teams..."
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Content *</label>
                <textarea
                  required
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={createForm.content}
                  onChange={(e) => setCreateForm({ ...createForm, content: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usage Guide</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="How to use this prompt responsibly..."
                  value={createForm.usage_guide}
                  onChange={(e) => setCreateForm({ ...createForm, usage_guide: e.target.value })}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{isSubmitting ? 'Creating...' : 'Create Prompt'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Prompt Modal */}
      {showEditModal && editingPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Prompt</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleEditPrompt} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                />
              </div>
              
              <MultiSelect
                label="Categories"
                options={categories}
                selected={editForm.category || []}
                onChange={(selected) => setEditForm({ ...editForm, category: selected })}
                placeholder="Select categories..."
              />

              <MultiSelect
                label="Teams"
                options={teams}
                selected={editForm.team || []}
                onChange={(selected) => setEditForm({ ...editForm, team: selected })}
                placeholder="Select teams..."
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Content</label>
                <textarea
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.content || ''}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usage Guide</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="How to use this prompt responsibly..."
                  value={editForm.usage_guide || ''}
                  onChange={(e) => setEditForm({ ...editForm, usage_guide: e.target.value })}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{isSubmitting ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Prompt Detail Modal */}
      {selectedPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedPrompt.title}
              </h3>
              <button
                onClick={() => setSelectedPrompt(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedPrompt.category && selectedPrompt.category.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                    <div className="flex flex-wrap gap-1">
                      {selectedPrompt.category.map((cat, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedPrompt.team && selectedPrompt.team.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teams</label>
                    <div className="flex flex-wrap gap-1">
                      {selectedPrompt.team.map((team, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {team}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedPrompt.description && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <p className="text-sm text-gray-900">{selectedPrompt.description}</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Content</label>
                <div className="bg-gray-50 rounded-md p-4 max-h-96 overflow-y-auto">
                  <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                    {selectedPrompt.content}
                  </pre>
                </div>
              </div>

              {selectedPrompt.usage_guide && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usage Guide</label>
                  <div className="bg-blue-50 rounded-md p-4 border border-blue-200">
                    <pre className="text-sm text-blue-900 whitespace-pre-wrap">
                      {selectedPrompt.usage_guide}
                    </pre>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => openEditModal(selectedPrompt)}
                className="btn-secondary flex items-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={() => setSelectedPrompt(null)}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prompts; 